/**
 * ProfileMenu Component
 *
 * Dropdown menu that appears when clicking the profile button
 * Shows login option if user is not logged in
 */

import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useAuthStore } from '@/store/authStore';
import { useAuthTokens } from '@/utils/tokenHelper';
import { useRedirectStore } from '@/store/redirectStore';
import { useUserStore } from '@/store/userStore';
import { useSearchStore } from '@/store/searchStore';
import { useChatScreenStore } from '@/store/chatScreenStore';
import { saveChatState, saveBookingState } from '@/utils/preLoginState';
import {
  typographyColors,
  backgroundColors,
  spacing,
  typography,
  fontPrimary,
  strokeColors,
  borders,
} from '@/design-system/utils';

export interface ProfileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  anchorElement?: HTMLElement | null;
  position?: 'bottom' | 'top';
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({
  isOpen,
  onClose,
  anchorElement,
  position = 'bottom',
}) => {
  const router = useRouter();
  const menuRef = useRef<HTMLDivElement>(null);
  const { accessToken, refreshToken } = useAuthTokens();
  const clearTokens = useAuthStore(state => state.clearTokens);
  const clearUserProfile = useUserStore(state => state.clearUserProfile);
  const { name, email } = useUserStore();
  const { resetSearchCount, setShowLoginPopup, setLoginRequired, setLoggedIn } =
    useSearchStore();
  const { setRedirectUrl, setLoginContext } = useRedirectStore();
  const { getActiveConversation } = useChatScreenStore();

  const isLoggedIn = !!accessToken && !!refreshToken;

  // Get display name - use full name if available, otherwise use default
  const isDefaultName = name === 'User Name';
  const displayName = isDefaultName ? 'User' : name || 'User';

  // Show profile section when we have real profile (even if tokens missing after redirect)
  const hasProfile =
    !isDefaultName && (name || (email && email !== 'user@example.com'));

  // Debug: log when menu is open so we can see why Login vs Login again vs Log Out
  useEffect(() => {
    if (!isOpen) return;
    console.log('[PROFILE_MENU_DEBUG] Menu opened', {
      isLoggedIn,
      hasProfile,
      name,
      email: email?.slice(0, 20) + (email && email.length > 20 ? '...' : ''),
      isDefaultName,
      buttonLabel: isLoggedIn
        ? 'Log Out'
        : hasProfile
          ? 'Login again'
          : 'Login',
    });
  }, [isOpen, isLoggedIn, hasProfile, name, email, isDefaultName]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        anchorElement &&
        !anchorElement.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, anchorElement]);

  // Position menu relative to anchor element
  useEffect(() => {
    if (!isOpen || !anchorElement || !menuRef.current) return;

    const updatePosition = () => {
      if (!anchorElement || !menuRef.current) return;

      const anchorRect = anchorElement.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = 0;
      let left = 0;

      if (position === 'bottom') {
        top = anchorRect.bottom + 8;
        left = anchorRect.left + anchorRect.width / 2 - menuRect.width / 2;
      } else {
        top = anchorRect.top - menuRect.height - 8;
        left = anchorRect.left + anchorRect.width / 2 - menuRect.width / 2;
      }

      // Ensure menu stays within viewport
      if (left < 8) left = 8;
      if (left + menuRect.width > viewportWidth - 8) {
        left = viewportWidth - menuRect.width - 8;
      }

      if (top < 8) top = anchorRect.bottom + 8; // Fallback to bottom if top doesn't fit
      if (top + menuRect.height > viewportHeight - 8) {
        top = anchorRect.top - menuRect.height - 8; // Fallback to top if bottom doesn't fit
      }

      menuRef.current.style.top = `${top}px`;
      menuRef.current.style.left = `${left}px`;
    };

    // Update position after menu is rendered
    // Use multiple requestAnimationFrame calls to ensure menu has dimensions
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updatePosition();
      });
    });

    // Also update on window resize and scroll
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, anchorElement, position]);

  const handleClick = async () => {
    // Log out when we have tokens or when we have profile (e.g. tokens not rehydrated but user data in store)
    if (isLoggedIn || hasProfile) {
      clearUserProfile();
      clearTokens();
      if (typeof window !== 'undefined') {
        localStorage.removeItem('user-profile-storage');
      }
      resetSearchCount();
      setShowLoginPopup(false);
      setLoginRequired(false);
      setLoggedIn(false);
      onClose();
      // Stay on the same page after logout (e.g. booking page, chat, or any current page)
      router.push(router.asPath);
      return;
    }

    // Not logged in and no profile: go to login
    {
      if (typeof window !== 'undefined') {
        const redirectPath = router.asPath;

        // Determine context based on current page
        const isChatPage = router.pathname === '/chat';
        const isBookingPage =
          router.pathname.includes('/activity-details') ||
          router.pathname.includes('/package-details') ||
          router.pathname.includes('/stay-experience-details');

        // Save chat state if on chat page
        if (isChatPage) {
          // Get sessionId from URL (API-returned session ID)
          // This is the real session_id from the API, not the client-generated conversationId
          const sessionIdFromUrl = router.query.sessionId as string | undefined;

          // Also check active conversation for sessionId
          const activeConversation = getActiveConversation();
          const apiSessionId =
            sessionIdFromUrl || activeConversation?.sessionId || null;

          if (apiSessionId) {
            // Get active tab from localStorage (use sessionId as key)
            let activeTab: string | undefined;
            try {
              const tabsStorage = localStorage.getItem('conversation-tabs');
              if (tabsStorage) {
                const tabs = JSON.parse(tabsStorage);
                // Try to find tab by sessionId first, then by conversationId
                activeTab =
                  tabs[apiSessionId] ||
                  (activeConversation?.id
                    ? tabs[activeConversation.id]
                    : undefined);
              }
            } catch (e) {
              console.warn('Failed to read active tab:', e);
            }

            // Use API sessionId as both conversationId and sessionId
            saveChatState(apiSessionId, apiSessionId, activeTab);

            console.log('💾 [ProfileMenu] Saved chat state before login:', {
              apiSessionId,
              clientConversationId: activeConversation?.id || 'none',
              activeTab,
              source: sessionIdFromUrl
                ? 'url'
                : activeConversation
                  ? 'store'
                  : 'none',
              note: 'Using API sessionId, not client-generated conversationId',
            });
          }

          setLoginContext('chat');
        }
        // Save booking state if on booking page
        else if (isBookingPage) {
          const slug = router.query.slug as string | undefined;
          // Determine experience type from pathname
          let experienceType: string | undefined;
          if (router.pathname.includes('/activity-details')) {
            experienceType = 'activity';
          } else if (router.pathname.includes('/package-details')) {
            experienceType = 'package';
          } else if (router.pathname.includes('/stay-experience-details')) {
            experienceType = 'stay';
          }

          saveBookingState(experienceType, slug);

          console.log('💾 [ProfileMenu] Saved booking state before login:', {
            experienceType,
            slug,
          });

          setLoginContext('booking');
        }
        // General login (not chat or booking)
        else {
          setLoginContext('general');
        }

        setRedirectUrl(redirectPath);
        window.location.href = `${process.env.NEXT_PUBLIC_API_URL}/auth/google/v1/login`;
      }
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className='fixed'
      style={{
        zIndex: 9999, // High z-index to ensure it appears above everything
        minWidth: '200px',
        borderRadius: borders.radius.md,
        border: `1px solid ${strokeColors.lightest}`,
        backgroundColor: backgroundColors.primary,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: spacing[2],
      }}
    >
      {isLoggedIn || hasProfile ? (
        <>
          {/* User Name Section - show when logged in or when we have profile (e.g. after redirect before tokens rehydrated) */}
          <div
            style={{
              padding: `${spacing[3]} ${spacing[4]}`,
              borderBottom: `1px solid ${strokeColors.lightest}`,
              marginBottom: spacing[2],
            }}
          >
            <div
              style={{
                fontFamily: fontPrimary,
                fontSize: typography.size.base,
                fontWeight: typography.weight.semibold,
                color: typographyColors.primary,
                marginBottom: spacing[1],
              }}
            >
              {displayName}
            </div>
            {email && email !== 'user@example.com' && (
              <div
                style={{
                  fontFamily: fontPrimary,
                  fontSize: typography.size.sm,
                  fontWeight: typography.weight.regular,
                  color: typographyColors.secondary,
                }}
              >
                {email.length > 25 ? `${email.slice(0, 25)}...` : email}
              </div>
            )}
          </div>
          {/* Log Out: clears tokens and user profile/localStorage, then navbar updates */}
          <button
            onClick={handleClick}
            style={{
              width: '100%',
              padding: `${spacing[3]} ${spacing[4]}`,
              borderRadius: borders.radius.sm,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: fontPrimary,
              fontSize: typography.size.base,
              fontWeight: typography.weight.medium,
              color: typographyColors.primary,
              transition: 'background-color 0.2s',
            }}
            className='hover:bg-gray-100'
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = backgroundColors.shade;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Log Out
          </button>
        </>
      ) : (
        <button
          onClick={handleClick}
          style={{
            width: '100%',
            padding: `${spacing[3]} ${spacing[4]}`,
            borderRadius: borders.radius.sm,
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: fontPrimary,
            fontSize: typography.size.base,
            fontWeight: typography.weight.medium,
            color: typographyColors.primary,
            transition: 'background-color 0.2s',
          }}
          className='hover:bg-gray-100'
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = backgroundColors.shade;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          Login
        </button>
      )}
    </div>
  );
};
